import { type MouseEvent } from "react";

import { Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { NodeTemplateScopeType } from "@skilitics/nautilus-endpoint-definition";

import type { NodeTemplatesSearchCriteria } from "../types";
import { Filter, FilterButton, FilterButtonGroup, FilterHeading, FilterIcon } from "./shared";

type Scope = NodeTemplateScopeType | "all";

const scopes: Scope[] = ["all", "user", "module", "system"];

type Props = {
  scope?: Scope;
  onChange: (newCriteria: NodeTemplatesSearchCriteria) => void;
  variant?: "default" | "noHeader";
};

export const Scope = ({ scope = "all", onChange, variant }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const handleChange = (_event: MouseEvent<HTMLElement>, selection: string) =>
    onChange({
      scope: selection === null || selection === "all" ? undefined : (selection as NodeTemplateScopeType),
    });

  return (
    <Filter data-test="scope-container">
      {variant !== "noHeader" && (
        <Tooltip title={formatMessage(`scopeTooltip`)} placement="top">
          <FilterHeading>{formatMessage("scope")}</FilterHeading>
        </Tooltip>
      )}
      <FilterButtonGroup value={scope} exclusive orientation="vertical" onChange={handleChange}>
        {scopes.map((value) => (
          <ScopeFilterButton
            key={value}
            scope={value}
            title={formatMessage(value)}
            tooltip={formatMessage(`${value}Tooltip`)}
          />
        ))}
      </FilterButtonGroup>
    </Filter>
  );
};

type ScopeFilterButtonProps = { scope: Scope; tooltip: string; title: string; className?: string };

const ScopeFilterButton = ({ scope, tooltip, title, className }: ScopeFilterButtonProps) => (
  <Tooltip title={tooltip} placement="top">
    <FilterButton value={scope} size="small" disableRipple data-test={`scope-${scope}`} className={className}>
      <FilterIcon icon="tick" />
      {title}
    </FilterButton>
  </Tooltip>
);

const formatStrings = {
  scope: {
    en: "Scope",
  },
  scopeTooltip: {
    en: "The availability of templates",
  },
  all: {
    en: "All",
  },
  allTooltip: {
    en: "All Templates",
  },
  user: {
    en: "User",
  },
  userTooltip: {
    en: "Only your templates.",
  },
  module: {
    en: "Module",
  },
  moduleTooltip: {
    en: "Templates scoped to this module",
  },
  system: {
    en: "Skilitics",
  },
  systemTooltip: {
    en: "Templates available to all users anywhere",
  },
  workspace: {
    en: "Workspace",
  },
  workspaceTooltip: {
    en: "Templates available to all users in the workspace",
  },
} satisfies FormatMessages;
